import React from 'react';
import {
  PencilIcon,
  TrashIcon,
  BookmarkIcon,
  ArchiveBoxIcon,
  BellIcon,
  CalendarIcon,
  TagIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import { Note, formatRelativeTime, getPriorityColor, getCategoryColor } from '../../api/notes';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';

interface ViewNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  note: Note | null;
  onEdit: () => void;
  onDelete: () => void;
  onTogglePin: (note: Note) => void;
  onToggleArchive: (note: Note) => void;
}

const ViewNoteModal: React.FC<ViewNoteModalProps> = ({
  isOpen,
  onClose,
  note,
  onEdit,
  onDelete,
  onTogglePin,
  onToggleArchive,
}) => {
  if (!note) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusBits = [
    note.isPinned ? 'Pinned' : null,
    note.isArchived ? 'Archived' : null,
    note.reminderDate ? 'Has Reminder' : null,
  ].filter(Boolean).join(' · ');

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader
        title={
          <span className="flex items-center gap-3">
            <ClipboardDocumentListIcon className="h-6 w-6 shrink-0 text-slate-400" />
            <span className="min-w-0">{note.title}</span>
          </span>
        }
        subtitle={statusBits || undefined}
        onClose={onClose}
      />
      <BrandModalBody>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Note Content */}
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-slate-900 mb-3">Content</h4>
                  <div className="bg-slate-50/80 rounded-lg p-4">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                      {note.content}
                    </p>
                  </div>
                </div>

                {/* Tags */}
                {note.tags && note.tags.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-3 flex items-center">
                      <TagIcon className="h-4 w-4 mr-1" />
                      Tags
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {note.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={onEdit}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit
                  </button>

                  <button
                    onClick={() => onTogglePin(note)}
                    className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 ${
                      note.isPinned ? 'text-cyan-700' : 'text-slate-700'
                    }`}
                  >
                    {note.isPinned ? (
                      <BookmarkSolidIcon className="h-4 w-4 mr-2" />
                    ) : (
                      <BookmarkIcon className="h-4 w-4 mr-2" />
                    )}
                    {note.isPinned ? 'Unpin' : 'Pin'}
                  </button>

                  <button
                    onClick={() => onToggleArchive(note)}
                    className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 ${
                      note.isArchived ? 'text-yellow-700' : 'text-slate-700'
                    }`}
                  >
                    <ArchiveBoxIcon className="h-4 w-4 mr-2" />
                    {note.isArchived ? 'Unarchive' : 'Archive'}
                  </button>

                  <button
                    onClick={onDelete}
                    className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                </div>
              </div>

              {/* Note Information */}
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h4 className="text-sm font-medium text-slate-900 mb-3">Information</h4>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-slate-500">Category</dt>
                      <dd className="mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(note.category)}`}>
                          {note.category.charAt(0).toUpperCase() + note.category.slice(1)}
                        </span>
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-slate-500">Priority</dt>
                      <dd className="mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(note.priority)}`}>
                          {note.priority.charAt(0).toUpperCase() + note.priority.slice(1)}
                        </span>
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-slate-500">Created</dt>
                      <dd className="text-sm text-slate-900 flex items-center mt-1">
                        <CalendarIcon className="h-4 w-4 mr-1 text-slate-400" />
                        <div>
                          <div>{formatDate(note.createdAt)}</div>
                          <div className="text-xs text-slate-500">{formatRelativeTime(note.createdAt)}</div>
                        </div>
                      </dd>
                    </div>

                    {note.updatedAt !== note.createdAt && (
                      <div>
                        <dt className="text-sm font-medium text-slate-500">Last Updated</dt>
                        <dd className="text-sm text-slate-900 flex items-center mt-1">
                          <CalendarIcon className="h-4 w-4 mr-1 text-slate-400" />
                          <div>
                            <div>{formatDate(note.updatedAt)}</div>
                            <div className="text-xs text-slate-500">{formatRelativeTime(note.updatedAt)}</div>
                          </div>
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>

                {/* Associations */}
                {(note.birdId || note.speciesId) && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-3">Associations</h4>
                    <dl className="space-y-2">
                      {note.birdId && (
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Bird ID</dt>
                          <dd className="text-sm text-slate-900">{note.birdId}</dd>
                        </div>
                      )}

                      {note.speciesId && (
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Species ID</dt>
                          <dd className="text-sm text-slate-900">{note.speciesId}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}

                {/* Reminder */}
                {note.reminderDate && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-3 flex items-center">
                      <BellIcon className="h-4 w-4 mr-1" />
                      Reminder
                    </h4>
                    <div className="text-sm text-slate-900">
                      <div>{formatDate(note.reminderDate)}</div>
                      <div className="text-xs text-slate-500">{formatRelativeTime(note.reminderDate)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
      </BrandModalBody>

      <BrandModalFooter>
        <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
          Close
        </button>
        <button type="button" onClick={onEdit} className="btn btn-primary text-sm">
          Edit
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
};

export default ViewNoteModal; 